import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/Button';
import { 
  RefreshCw, Play, Clock, Mail, Trash2, Download, Info, Users, 
  Check, X, History, Settings, Calendar, Server, Link 
} from 'lucide-react';
import { useAutoSync } from 'contexts/AutoSyncContext';
import { teamsApi, offersApi, applicationsApi, uploadsApi, type Team, type Offer } from 'lib/api';
import { useNotifications } from 'contexts/NotificationContext';
import { PageHeader } from 'components/layout/PageHeader';

export default function AutoSyncCenter() {
  const { t } = useTranslation();
  const { addNotification } = useNotifications();
  
  const { 
    isSyncActive, setIsSyncActive, 
    syncInterval, setSyncInterval, 
    autoSyncTeamId, setAutoSyncTeamId,
    lastSyncTime, pendingFiles, 
    isSyncing, isConnected, 
    triggerSync, handleImportPending, handleDeletePending 
  } = useAutoSync();

  const [teams, setTeams] = useState<Team[]>([]);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [selectedOffers, setSelectedOffers] = useState<Record<string, number>>({});
  
  // Mettre à jour `selectedOffers` dès qu'un fichier a un `suggested_offer_id`
  useEffect(() => {
    if (pendingFiles && pendingFiles.length > 0) {
      setSelectedOffers(prev => {
        const next = { ...prev };
        let hasChanges = false;
        pendingFiles.forEach(file => {
          if (file.suggested_offer_id && !next[file.id]) {
            next[file.id] = file.suggested_offer_id;
            hasChanges = true;
          }
        });
        return hasChanges ? next : prev;
      });
    }
  }, [pendingFiles]);
  
  const [activeTab, setActiveTab] = useState('settings');
  const [daysToFetch, setDaysToFetch] = useState<number>(1);
  const [logs, setLogs] = useState<any[]>([]);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);

  // Connection State
  const [emailConnection, setEmailConnection] = useState<any>(() => {
    const connStr = localStorage.getItem('selektia_email_connection');
    return connStr ? JSON.parse(connStr) : null;
  });
  const [selectedProvider, setSelectedProvider] = useState<'gmail' | 'imap' | 'outlook'>('gmail');
  const [isConnectingGmail, setIsConnectingGmail] = useState(false);
  const [isConnectingImap, setIsConnectingImap] = useState(false);
  const [rememberConnection, setRememberConnection] = useState(true);
  const [imapConfig, setImapConfig] = useState({
    host: '',
    port: 993,
    username: '',
    password: '',
    folder: 'INBOX',
    use_ssl: true,
  });

  useEffect(() => {
    const updateConnection = () => {
      const connStr = localStorage.getItem('selektia_email_connection');
      setEmailConnection(connStr ? JSON.parse(connStr) : null);
    };
    window.addEventListener('storage', updateConnection);
    return () => window.removeEventListener('storage', updateConnection);
  }, []);

  const handleConnectGmail = async () => {
    try {
      setIsConnectingGmail(true);
      const response = await uploadsApi.getGmailAuthUrl();
      const width = 500; const height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      const popup = window.open(response.data.auth_url, 'Gmail Auth', `width=${width},height=${height},left=${left},top=${top}`);
      const checkPopup = setInterval(() => {
        if (!popup || popup.closed) {
          clearInterval(checkPopup);
          setIsConnectingGmail(false);
        }
      }, 500);
    } catch (e) {
      setIsConnectingGmail(false);
      addNotification({ type: 'error', title: t('common.error', 'Error'), message: t('candidates.emailImport.connectFailed', 'Failed to connect to Gmail.') });
    }
  };

  const handleConnectImap = async () => {
    try {
      setIsConnectingImap(true);
      
      if (selectedProvider === 'outlook') {
        const response = await uploadsApi.connectOutlookMailbox({ 
          username: imapConfig.username, 
          password: imapConfig.password, 
          folder: imapConfig.folder 
        });
        const newConn = { 
          provider: 'exchange', 
          email: response.data.email || imapConfig.username, 
          remember: rememberConnection,
          expiry: rememberConnection ? undefined : Date.now() + 3600 * 1000 
        };
        localStorage.setItem('selektia_email_connection', JSON.stringify(newConn));
        setEmailConnection(newConn);
        window.dispatchEvent(new Event('storage'));
        addNotification({ type: 'success', title: t('common.success', 'Success'), message: 'Connecté avec Outlook/Exchange avec succès' });
        return;
      }
      
      const response = await uploadsApi.connectImapMailbox(imapConfig);
      const newConn = {
        provider: 'imap',
        email: response.data?.email || imapConfig.username,
        remember: rememberConnection,
        expiry: rememberConnection ? undefined : Date.now() + 3600 * 1000
      };
      localStorage.setItem('selektia_email_connection', JSON.stringify(newConn));
      setEmailConnection(newConn);
      window.dispatchEvent(new Event('storage'));
      addNotification({ type: 'success', title: t('common.success', 'Success'), message: t('candidates.emailImport.imapConnectedSuccess', 'Successfully connected to IMAP mailbox.') });
    } catch (e) {
      addNotification({ type: 'error', title: t('common.error', 'Error'), message: t('candidates.emailImport.imapConnectFailed', 'Failed to connect to IMAP mailbox.') });
    } finally {
      setIsConnectingImap(false);
    }
  };

  const handleDisconnect = () => {
    localStorage.removeItem('selektia_email_connection');
    localStorage.removeItem('selektia_auto_sync_active');
    localStorage.removeItem('selektia_auto_sync_interval');
    localStorage.removeItem('selektia_auto_sync_pending');
    localStorage.removeItem('selektia_auto_sync_last');
    setEmailConnection(null);
    window.dispatchEvent(new Event('storage'));
  };

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'gmail_auth_success') {
        const newConn = { 
          provider: 'gmail', 
          email: event.data?.email, 
          remember: rememberConnection,
          expiry: rememberConnection ? undefined : Date.now() + 3600 * 1000
        };
        localStorage.setItem('selektia_email_connection', JSON.stringify(newConn));
        setEmailConnection(newConn);
        window.dispatchEvent(new Event('storage'));
        setIsConnectingGmail(false);
        addNotification({ type: 'success', title: t('common.success', 'Success'), message: t('candidates.emailImport.connectedSuccess', 'Successfully connected to Gmail') });
      }
      if (event.data?.type === 'gmail_auth_error') {
        setIsConnectingGmail(false);
        addNotification({ type: 'error', title: t('common.error', 'Error'), message: t('candidates.emailImport.connectFailed', 'Failed to connect to Gmail.') });
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [addNotification, rememberConnection, t]);

  useEffect(() => {
      teamsApi.getAll()
        .then(res => setTeams(res.data.results || []))
        .catch(() => {});
    }, []);

    const offersFetchedRef = useRef(false);
    useEffect(() => {
      if (!pendingFiles || pendingFiles.length === 0 || offersFetchedRef.current) return;
      
      offersFetchedRef.current = true;
      offersApi.getAllPages({ light: 'true' })
        .then(res => {
          if (res) setOffers(res);
        })
        .catch(() => {
          offersFetchedRef.current = false;
        });
    }, [pendingFiles]);
  useEffect(() => {
    if (activeTab === 'history') {
      setIsLoadingLogs(true);
      uploadsApi.getAutoSyncLogs({ limit: 50 })
        .then(res => setLogs(res.data?.results || []))
        .catch(() => addNotification({ type: 'error', title: t('common.error', 'Erreur'), message: t('candidates.sync.loadLogsError', 'Erreur lors du chargement des logs') }))
        .finally(() => setIsLoadingLogs(false));
    }
  }, [activeTab, addNotification, t]);

  const handleManualSync = () => {
    triggerSync(daysToFetch);
  };

  const handleConfirmAssignment = async (file: any) => {
    const offerId = selectedOffers[file.id];
    if (!offerId || !file.candidate_id) return;
    try {
      await applicationsApi.create({ candidate: file.candidate_id, offer: offerId });
      addNotification({ type: 'success', title: t('common.success', 'Success'), message: t('candidates.emailImport.assignedSuccess', 'Candidat affecté avec succès') });
      
      uploadsApi.logAutoSyncAction({
        action: 'assign_offer',
        filename: file.filename,
        sender: file.sender,
        subject: file.subject,
        candidate_id: file.candidate_id,
        offer_id: offerId,
        status: 'success'
      }).catch(() => {});

      handleDeletePending(file, false);
    } catch (e: any) {
      addNotification({ type: 'error', title: t('common.error', 'Error'), message: e.response?.data?.message || t('candidates.emailImport.assignError', "Erreur lors de l'affectation") });
      
      uploadsApi.logAutoSyncAction({
        action: 'assign_offer',
        filename: file.filename,
        sender: file.sender,
        subject: file.subject,
        candidate_id: file.candidate_id,
        offer_id: offerId,
        status: 'failed',
        details: e.response?.data?.message || t('candidates.emailImport.assignError', "Erreur lors de l'affectation")
      }).catch(() => {});
    }
  };

  const handleRejectAssignment = (file: any) => {
    uploadsApi.logAutoSyncAction({
      action: 'reject_candidate',
      filename: file.filename,
      sender: file.sender,
      subject: file.subject,
      candidate_id: file.candidate_id,
      status: 'success'
    }).catch(() => {});

    handleDeletePending(file, false);
  };

  // --- Components ---
  const Badge = ({ children, variant = 'default', className = '' }: { children: React.ReactNode, variant?: string, className?: string }) => {
    const variants: Record<string, string> = {
      default: "bg-primary text-primary-foreground",
      success: "bg-green-100 text-green-800 border-green-200",
      warning: "bg-amber-100 text-amber-800 border-amber-200",
      destructive: "bg-red-100 text-red-800 border-red-200",
      outline: "bg-transparent border-input text-foreground"
    };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] sm:text-xs font-semibold border ${variants[variant] || variants.default} ${className}`}>
        {children}
      </span>
    );
  };

  if (!emailConnection) {
    return (
      <div className="p-6 max-w-4xl mx-auto space-y-6">
        <PageHeader title={t('candidates.sync.centerTitle', 'Centre de Synchronisation Automatique')} />
        <div className="rounded-xl border bg-card shadow-sm p-8 max-w-2xl mx-auto mt-10">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-foreground">
              {t('candidates.emailImport.connectTitle', 'Connecter une boîte email')}
            </h2>
            <p className="text-sm text-muted-foreground mt-2">
              {t('candidates.sync.centerDesc', "Gérez vos synchronisations d'emails et l'historique de traitement.")}
            </p>
          </div>

          <div className="grid grid-cols-3 gap-3 mb-6">
            <button type="button" onClick={() => setSelectedProvider('gmail')} className={`flex items-center justify-center gap-2 rounded-md border px-4 py-3 text-sm transition-colors ${selectedProvider === 'gmail' ? 'border-primary bg-primary/10 text-primary font-medium' : 'border-border bg-background text-foreground hover:bg-muted/50'}`}>
              <Mail className="h-5 w-5" /> Google Workspace
            </button>
            <button type="button" onClick={() => setSelectedProvider('outlook')} className={`flex items-center justify-center gap-2 rounded-md border px-4 py-3 text-sm transition-colors ${selectedProvider === 'outlook' ? 'border-primary bg-primary/10 text-primary font-medium' : 'border-border bg-background text-foreground hover:bg-muted/50'}`}>
              <Server className="h-5 w-5" /> Exchange / Outlook
            </button>
            <button type="button" onClick={() => setSelectedProvider('imap')} className={`flex items-center justify-center gap-2 rounded-md border px-4 py-3 text-sm transition-colors ${selectedProvider === 'imap' ? 'border-primary bg-primary/10 text-primary font-medium' : 'border-border bg-background text-foreground hover:bg-muted/50'}`}>
              <Server className="h-5 w-5" /> Boîte pro (IMAP)
            </button>
          </div>

          {selectedProvider === 'gmail' ? (
            <div className="flex flex-col items-center justify-center p-8 border-2 border-dashed rounded-lg bg-muted/20">
               <Mail className="h-12 w-12 text-muted-foreground mb-4" />
               <p className="text-sm text-center text-muted-foreground max-w-md mb-6">{t('candidates.emailImport.description', 'Connect your Gmail account to retrieve CV files from your emails and add them as candidates.')}</p>
               
               <label className="flex items-center gap-2 mb-6 text-sm text-muted-foreground cursor-pointer">
                 <input type="checkbox" checked={rememberConnection} onChange={e => setRememberConnection(e.target.checked)} className="h-4 w-4 rounded border-input cursor-pointer" />
                 {t('candidates.emailImport.rememberConnection', 'Se souvenir de la connexion')}
               </label>

               <Button type="button" onClick={handleConnectGmail} disabled={isConnectingGmail} className="w-full sm:w-auto min-w-[250px] h-10">
                 {isConnectingGmail ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}
                 {t('candidates.emailImport.connectButton', 'Connect Google Account')}
               </Button>
            </div>
          ) : (
            <div className="space-y-4 p-5 border rounded-lg bg-muted/10">
           <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                 {selectedProvider !== 'outlook' && (
                 <>
                 <div className="space-y-1.5">
                   <label className="text-xs font-medium">{t('candidates.emailImport.imapHost', 'Hôte IMAP')}</label>
                   <input type="text" placeholder="imap.entreprise.com" value={imapConfig.host} onChange={e => setImapConfig({...imapConfig, host: e.target.value})} disabled={isConnectingImap} className="flex h-10 w-full rounded-md border border-input px-3 text-sm bg-background" />
                 </div>
                 
                 <div className="space-y-1.5">
                   <label className="text-xs font-medium">{t('candidates.emailImport.imapPort', 'Port')}</label>
                   <input type="number" value={imapConfig.port} onChange={e => setImapConfig({...imapConfig, port: Number(e.target.value) || 993})} disabled={isConnectingImap} className="flex h-10 w-full rounded-md border border-input px-3 text-sm bg-background" />
                 </div>
                 </>
                 )}

                 <div className="space-y-1.5">
                   <label className="text-xs font-medium">{t('candidates.emailImport.imapUsername', 'Email / Utilisateur')}</label>
                   <input type="text" placeholder="email@entreprise.com" value={imapConfig.username} onChange={e => setImapConfig({...imapConfig, username: e.target.value})} disabled={isConnectingImap} className="flex h-10 w-full rounded-md border border-input px-3 text-sm bg-background" />
                 </div>
                 
                 <div className="space-y-1.5">
                   <label className="text-xs font-medium">{t('candidates.emailImport.imapPassword', 'Mot de passe')}</label>
                   <input type="password" value={imapConfig.password} onChange={e => setImapConfig({...imapConfig, password: e.target.value})} disabled={isConnectingImap} className="flex h-10 w-full rounded-md border border-input px-3 text-sm bg-background" />
                 </div>

                 <div className="space-y-1.5">
                   <label className="text-xs font-medium">{t('candidates.emailImport.imapFolder', 'Dossier (Folder)')}</label>
                   <input type="text" placeholder="INBOX" value={imapConfig.folder || ''} onChange={e => setImapConfig({...imapConfig, folder: e.target.value})} disabled={isConnectingImap} className="flex h-10 w-full rounded-md border border-input px-3 text-sm bg-background" />
                 </div>
                 
                 {selectedProvider !== 'outlook' && (
                 <div className="space-y-1.5 flex flex-col justify-end pb-3">
                   <label className="flex items-center gap-2 text-sm font-medium whitespace-nowrap cursor-pointer">
                     <input type="checkbox" checked={imapConfig.use_ssl} onChange={e => setImapConfig({...imapConfig, use_ssl: e.target.checked})} disabled={isConnectingImap} className="h-4 w-4 rounded border-input cursor-pointer" />
                     {t('candidates.emailImport.imapUseSsl', 'Utiliser SSL/TLS')}
                   </label>
                 </div>
                 )}
               </div>
               
               <label className="flex items-center gap-2 pt-2 text-sm text-muted-foreground w-fit cursor-pointer">
                 <input type="checkbox" checked={rememberConnection} onChange={e => setRememberConnection(e.target.checked)} className="h-4 w-4 rounded border-input cursor-pointer" />
                 {t('candidates.emailImport.rememberConnection', 'Se souvenir de la connexion')}
               </label>

               <div className="flex justify-end pt-2">
                 <Button type="button" onClick={handleConnectImap} disabled={isConnectingImap || (!imapConfig.host && selectedProvider !== 'outlook') || !imapConfig.username || !imapConfig.password} className="w-full sm:w-auto min-w-[200px] h-10">
                    {isConnectingImap ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <Server className="mr-2 h-4 w-4" />}
                    {t('common.connect', 'Connecter')}
                 </Button>
               </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <PageHeader
          title={
            <span className="flex items-center gap-2">
              <RefreshCw
                className={`h-6 w-6 ${isSyncing ? 'animate-spin text-primary' : isSyncActive ? 'animate-[spin_3s_linear_infinite] text-primary' : 'text-muted-foreground'}`}
              />
              {t('candidates.sync.centerTitle', 'Centre de Synchronisation Automatique')}
            </span>
          }
          description={t('candidates.sync.centerDesc', "Gérez vos synchronisations d'emails et l'historique de traitement.")}
        />
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 rounded-lg bg-muted p-1 max-w-fit">
        <button
          onClick={() => setActiveTab('settings')}
          className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-all ${activeTab === 'settings' ? 'bg-background text-foreground shadow' : 'text-muted-foreground hover:bg-background/50'}`}
        >
          <Settings className="h-4 w-4" />
          {t('candidates.sync.tabSettings', 'Paramètres & Attentes')}
          {pendingFiles.length > 0 && (
            <span className="ml-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] text-primary-foreground">
              {pendingFiles.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-all ${activeTab === 'history' ? 'bg-background text-foreground shadow' : 'text-muted-foreground hover:bg-background/50'}`}
        >
          <History className="h-4 w-4" />
          {t('candidates.sync.tabHistory', 'Historique')}
        </button>
      </div>

      {activeTab === 'settings' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
          
          {/* Left Column: Connection Info & Settings */}
          <div className="space-y-6">
            
            {/* Connection Card */}
            <div className="rounded-xl border bg-card shadow-sm p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-[15px] font-semibold flex items-center gap-2 text-foreground">
                  <Server className="h-4 w-4 text-primary" />
                  {t('candidates.sync.boxConnected', 'Boîte Mail Connectée')}
                </h2>
                <Button variant="outline" size="sm" onClick={handleDisconnect} className="h-7 text-xs px-2 text-destructive border-destructive/20 hover:bg-destructive/10">
                  <X className="h-3.5 w-3.5 mr-1" />
                  {t('common.disconnect', 'Déconnecter')}
                </Button>
              </div>
              <div className="space-y-2.5 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">{t('candidates.sync.provider', 'Fournisseur :')}</span>
                  <div className="inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold font-medium bg-background text-foreground shadow-sm">{String(emailConnection.provider).toUpperCase()}</div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">{t('candidates.sync.email', 'Email :')}</span>
                  <span className="font-medium truncate max-w-[150px]">{emailConnection.email || emailConnection.user}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">{t('candidates.sync.status', 'Statut :')}</span>
                  <span className="flex items-center gap-1.5 text-xs font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded-full border border-green-200">
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-500"></span>
                    </span>
                    {t('candidates.sync.active', 'Actif')}
                  </span>
                </div>
              </div>
            </div>

            {/* Sync Settings Card */}
            <div className={`rounded-xl border bg-card shadow-sm p-5 space-y-6 ${!emailConnection ? 'opacity-50 pointer-events-none' : ''}`}>
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-[15px] font-semibold flex items-center gap-2 text-foreground relative">
                    <Settings className="h-4 w-4 text-primary" />
                    {t('candidates.sync.settings', 'Paramètres Globaux')}
                    <div className="relative group flex items-center cursor-help">
                      <Info className="h-4 w-4 text-muted-foreground/60 hover:text-foreground transition-colors" />
                      <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 hidden group-hover:block w-64 p-2 bg-slate-800 text-white text-xs font-normal text-center rounded shadow-lg z-50 pointer-events-none">
                        {t('candidates.emailImport.autoSyncInfo', "L'auto-sync récupère les candidats reçus dans les dernières 24 heures. Pour les candidats plus anciens, allez dans Candidats > Import via Email.")}
                        <div className="absolute -top-1 left-1/2 -translate-x-1/2 border-4 border-transparent border-b-slate-800" />
                      </div>
                    </div>
                  </h2>
                  <button
                    onClick={() => setIsSyncActive(!isSyncActive)}
                    className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${isSyncActive ? 'bg-primary' : 'bg-muted'}`}
                    role="switch"
                    aria-checked={isSyncActive}
                  >
                    <span aria-hidden="true" className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isSyncActive ? 'translate-x-4' : 'translate-x-0'}`} />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">{t('candidates.sync.interval', 'Intervalle')}</span>
                    <div className="flex items-center gap-2 ml-auto">
                      <input 
                        type="number" 
                        min={1} max={60} 
                        value={syncInterval}
                        disabled={isSyncActive}
                        onChange={e => setSyncInterval(Number(e.target.value) || 5)}
                        className="w-16 h-8 rounded-md border border-input px-2 text-sm text-center focus:ring-1 focus:ring-primary outline-none disabled:opacity-50 disabled:bg-muted disabled:cursor-not-allowed"
                      />
                      <span className="text-xs text-muted-foreground">min</span>
                    </div>
                  </div>

                  <div className="space-y-2 mt-2">
                    <span className="text-sm font-medium flex items-center gap-2">
                      <Users className="h-4 w-4 text-muted-foreground" />
                      {t('candidates.sync.targetGroup', 'Importer dans le groupe')}
                    </span>
                    <select
                      className="w-full h-9 text-sm rounded-md border border-input bg-background px-3 focus:ring-1 focus:ring-primary outline-none disabled:opacity-50 disabled:bg-muted disabled:cursor-not-allowed"
                      value={autoSyncTeamId || ''}
                      disabled={isSyncActive}
                      onChange={(e) => setAutoSyncTeamId(e.target.value ? Number(e.target.value) : null)}
                    >
                        <option value="">{t('candidates.sync.allGroups', 'Aucun (Global)')}</option>
                      {teams.map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="pt-5 border-t border-border/50 space-y-4">
                <div className="space-y-2">
                  <span className="text-sm font-medium flex items-center gap-2 text-foreground">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    {t('candidates.sync.searchPeriod', 'Période de recherche (Manuel)')}
                  </span>
                  <select
                    className="w-full h-9 text-sm rounded-md border border-input bg-background px-3 focus:ring-1 focus:ring-primary outline-none"
                    value={daysToFetch}
                    onChange={(e) => setDaysToFetch(Number(e.target.value))}
                  >
                    <option value={1}>{t('candidates.sync.last24h', 'Dernières 24 Heures')}</option>
                    <option value={7}>{t('candidates.sync.last7Days', '7 Derniers Jours')}</option>
                    <option value={15}>{t('candidates.sync.last15Days', '15 Derniers Jours')}</option>
                    <option value={30}>{t('candidates.sync.last30Days', '30 Derniers Jours')}</option>
                  </select>
                </div>
                
                <div className="flex flex-col gap-2 pt-2">
                  <Button onClick={handleManualSync} disabled={isSyncing} className="w-full h-9 shadow-sm">
                    <Play className={`h-4 w-4 mr-2 ${isSyncing ? 'animate-pulse' : ''}`} /> 
                    {isSyncing ? t('candidates.sync.syncing', 'Synchronisation...') : t('candidates.sync.syncNow', 'Lancer la Synchro')}
                  </Button>
                  <span className="text-[11px] text-muted-foreground text-center mt-1">
                    {lastSyncTime ? `${t('candidates.sync.lastSync', 'Dernière synchro')} : ${lastSyncTime}` : t('candidates.sync.neverSynced', 'Jamais synchronisé')}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Pending Files */}
          <div className="lg:col-span-2 rounded-xl border bg-card shadow-sm flex flex-col h-[680px] overflow-hidden">
            <div className="p-4 border-b flex items-center justify-between bg-muted/20">
              <h2 className="text-[15px] font-semibold flex items-center gap-2 text-foreground">
                <Mail className="h-4 w-4 text-primary" />
                {t('candidates.sync.pendingEmails', 'Fichiers en attente')}
              </h2>
              {pendingFiles.length > 0 && (
                <Badge variant="outline" className="bg-background font-medium">
                  {t('candidates.sync.filesCount', '{{count}} fichier', { count: pendingFiles.length, count_plural: '{{count}} fichiers' })}
                </Badge>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-4 bg-muted/5">
              {pendingFiles.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-muted-foreground space-y-3">
                  <div className="h-16 w-16 rounded-full bg-muted/50 flex items-center justify-center mb-2">
                    <Mail className="h-8 w-8 opacity-40" />
                  </div>
                  <p className="text-sm">{t('candidates.emailImport.noPendingEmails', 'Aucun fichier en attente')}</p>
                </div>
              ) : (
                <ul className="space-y-3">
                  {pendingFiles.map(file => (
                    <li key={file.id} className="p-3.5 rounded-lg border bg-background shadow-sm hover:shadow-md hover:border-primary/30 transition-all group">
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0 flex-1 space-y-1.5">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold text-[13px] truncate text-foreground" title={file.filename}>
                              {file.filename}
                            </p>
                            {file.status === 'importing' && (
                              <Badge variant="outline"><RefreshCw className="h-3 w-3 mr-1 animate-spin inline" /> {t('candidates.sync.inProgress', 'En cours')}</Badge>
                            )}
                            {file.status === 'imported' && (
                              <Badge variant="success">{t('candidates.sync.imported', 'Importé')}</Badge>
                            )}
                            {file.status === 'error' && (
                              <Badge variant="destructive">{t('candidates.sync.errorLog', 'Erreur')}</Badge>
                            )}
                            {file.is_application === false && !file.is_duplicate && (
                              <Badge variant="warning">{t('candidates.sync.nonCvMail', 'Mail normal détecté')}</Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground truncate">
                            <span className="font-medium text-foreground/80">{file.sender}</span> <span className="text-muted-foreground/30 mx-1">•</span> {file.subject}
                          </p>
                        </div>

                        <div className="flex items-center gap-1.5 shrink-0 opacity-80 group-hover:opacity-100 transition-opacity">
                          {file.status !== 'imported' && file.status !== 'importing' && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleImportPending(file)} 
                              disabled={file.status === 'importing'}
                              className="h-8 w-8 p-0 text-primary border-primary/20 hover:bg-primary/10"
                              title={t('common.import', 'Import')}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          )}
                          {file.status !== 'imported' && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleDeletePending(file)} 
                              disabled={file.status === 'importing'}
                              className="h-8 w-8 p-0 text-destructive hover:bg-destructive/10"
                              title={t('common.delete', 'Supprimer')}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>

                      {file.status === 'imported' && file.candidate_id && (
                        <div className="mt-3 pt-3 border-t border-border/60 flex flex-col sm:flex-row items-center gap-2">
                          <select 
                            className="flex-1 w-full h-[34px] text-xs rounded-md border border-input bg-background px-2.5 focus:ring-1 focus:ring-primary outline-none"
                            value={selectedOffers[file.id] || ''}
                            onChange={(e) => setSelectedOffers(prev => ({ ...prev, [file.id]: Number(e.target.value) }))}
                          >
                            <option value="" className="text-muted-foreground">{t('candidates.sync.selectOffer', "Sélectionner l'offre à affecter...")}</option>
                            {offers.map(o => (
                                <option key={o.id} value={o.id}>{o.job_title} {o.location ? `- ${o.location}` : ''}</option>
                            ))}
                          </select>
                          <div className="flex items-center gap-2 shrink-0 w-full sm:w-auto">
                            <Button 
                              size="sm"
                              variant="outline"
                              disabled={!selectedOffers[file.id]}
                              onClick={() => handleConfirmAssignment(file)} 
                              className="flex-1 sm:flex-none h-[34px] text-xs font-medium border-green-200 text-green-700 bg-green-50/50 hover:bg-green-100/50 hover:border-green-300"
                            >
                              <Check className="h-3.5 w-3.5 mr-1" />
                              {t('candidates.sync.assign', 'Affecter')}
                            </Button>
                            <Button 
                              size="sm"
                              variant="outline"
                              onClick={() => handleRejectAssignment(file)} 
                              className="flex-1 sm:flex-none h-[34px] text-xs font-medium border-red-200 text-red-700 bg-red-50/50 hover:bg-red-100/50 hover:border-red-300 px-3"
                              title={t('candidates.sync.rejectHover', 'Rejeter (Garder CV sans offre)')}
                            >
                              <X className="h-3.5 w-3.5 mr-1" />
                              {t('candidates.sync.ignore', 'Ignorer')}
                            </Button>
                          </div>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="rounded-xl border bg-card shadow-sm animate-in fade-in slide-in-from-bottom-2 duration-300 overflow-hidden">
          <div className="p-4 border-b bg-muted/20 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <h2 className="text-[15px] font-semibold flex items-center gap-2 text-foreground">
                <History className="h-4 w-4 text-primary" />
                {t('candidates.sync.historyTitle', 'Historique des Synchronisations')}
              </h2>
              <p className="text-[13px] text-muted-foreground mt-0.5">{t('candidates.sync.historyDesc', "Détails des 50 dernières actions de traitement par l'AutoSync.")}</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => {
              setIsLoadingLogs(true);
              uploadsApi.getAutoSyncLogs({ limit: 50 }).then(res => setLogs(res.data?.results || [])).finally(() => setIsLoadingLogs(false));
            }} className="h-8 shadow-sm" disabled={isLoadingLogs}>
              <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${isLoadingLogs ? 'animate-spin' : ''}`} />
              {t('candidates.sync.refresh', 'Actualiser')}
            </Button>
          </div>
          
          <div className="overflow-x-auto min-h-[400px]">
            <table className="w-full text-sm text-left border-collapse">
              <thead className="text-[11px] text-muted-foreground bg-muted/40 uppercase tracking-wider font-semibold">
                <tr>
                  <th className="px-5 py-3 border-b">{t('candidates.sync.tableDate', 'Date & Heure')}</th>
                  <th className="px-5 py-3 border-b">{t('candidates.sync.tableAction', 'Action')}</th>
                  <th className="px-5 py-3 border-b">{t('candidates.sync.tableContent', "l'Email")}</th>
                  <th className="px-5 py-3 border-b">{t('candidates.sync.tableCandidate', 'Candidat')}</th>
                  <th className="px-5 py-3 border-b">{t('candidates.sync.tableAssignedOffer', 'Offre Affectée')}</th>
                  <th className="px-5 py-3 border-b text-center">{t('candidates.sync.tableStatus', 'Statut')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {isLoadingLogs ? (
                  <tr>
                    <td colSpan={6} className="px-5 py-24 text-center text-muted-foreground">
                      <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-3 opacity-50 text-primary" />
                      <p className="text-sm">{t('candidates.sync.loadingHistory', "Chargement de l'historique...")}</p>
                    </td>
                  </tr>
                ) : logs.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-5 py-24 text-center text-muted-foreground">
                      <div className="h-12 w-12 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-3">
                        <History className="h-5 w-5 opacity-40" />
                      </div>
                      <p className="text-sm">{t('candidates.sync.emptyHistory', 'Aucun historique de synchronisation trouvé.')}</p>
                    </td>
                  </tr>
                ) : (
                  logs.map((log: any) => (
                    <tr key={log.id} className="hover:bg-muted/30 transition-colors group">
                      <td className="px-5 py-3 align-middle text-[13px] text-muted-foreground whitespace-nowrap">
                        {new Date(log.created_at).toLocaleString()}
                      </td>
                      <td className="px-5 py-3 align-middle">
                        <span className="font-medium text-[13px] text-foreground">
                          {log.action === 'sync_triggered' ? t('candidates.sync.actionSync', 'Déclenchement Sync')
                            : log.action === 'import_success' ? t('candidates.sync.actionImport', 'Importation')
                            : log.action === 'assign_offer' ? t('candidates.sync.actionAssign', 'Affectation Offre')
                            : log.action === 'reject_candidate' ? t('candidates.sync.actionReject', "rejeté l'affectation")
                            : log.action}
                        </span>
                      </td>
                      <td className="px-5 py-3 align-middle">
                        <div className="flex flex-col max-w-[350px]">
                          <div className="flex items-center gap-1.5 font-medium text-[13px] text-foreground/90 truncate" title={log.filename}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-muted-foreground"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/><path d="M14 2v4a2 2 0 0 0 2 2h4"/><path d="M10 9H8"/><path d="M16 13H8"/><path d="M16 17H8"/></svg>
                            <span className="truncate">{log.filename || '---'}</span>
                          </div>
                          {log.subject && <span className="text-[11px] text-muted-foreground truncate mt-0.5" title={log.subject}>{t('candidates.sync.subject', 'Sujet:')} {log.subject}</span>}
                          {log.sender && <span className="text-[11px] text-muted-foreground truncate mt-0.5" title={log.sender}>{t('candidates.sync.from', 'De:')} {log.sender}</span>}
                          {log.details && (
                            <span 
                              className="text-[10px] text-muted-foreground/80 line-clamp-2 mt-1 italic leading-tight" 
                              title={log.details}
                            >
                              {log.status === 'failed' || log.status === 'error' ? t('candidates.sync.errorLog', 'Erreur : ') : ''}
                              {log.details}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-3 align-middle">
                        {log.candidate_id ? (
                            log.candidate_deleted ? (
                              <span className="text-muted-foreground text-[13px] line-through decoration-muted-foreground/50 opacity-70" title={t('candidates.sync.candidateDeletedHover', 'Candidat #{{id}} supprimé', { id: log.candidate_id })}>
                                {t('candidates.sync.deletedCandidate', 'Supprimé')}
                              </span>
                            ) : (
                              <a href={"/candidates/" + log.candidate_id} target="_blank" rel="noreferrer" className="text-primary hover:underline font-medium text-[13px]">
                                {log.candidate_name || t('candidates.sync.candidateId', 'Candidat #{{id}}', { id: log.candidate_id })}
                              </a>
                            )
                        ) : (
                          <span className="text-muted-foreground text-[13px]">â€”</span>
                        )}
                      </td>
                      <td className="px-5 py-3 align-middle">
                        {log.offer_id ? (
                            log.offer_deleted ? (
                              <span className="text-muted-foreground text-[13px] line-through decoration-muted-foreground/50 opacity-70" title={t('candidates.sync.offerDeletedHover', 'Offre #{{id}} supprimée', { id: log.offer_id })}>
                                {t('candidates.sync.deletedOffer', 'Supprimée')}
                              </span>
                            ) : (
                              <a href={"/offers/" + log.offer_id} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline font-medium text-[13px]">
                                {log.offer_title || offers.find(o => o.id === log.offer_id)?.job_title || t('candidates.sync.offerId', 'Offre #{{id}}', { id: log.offer_id })}
                              </a>
                            )
                        ) : (
                          <span className="text-muted-foreground text-[13px]">â€”</span>
                        )}
                      </td>
                      <td className="px-5 py-3 align-middle text-center">
                        {log.status === 'success' ? (
                          <Badge variant="success">{t('candidates.sync.statusSuccess', 'Succès')}</Badge>
                        ) : log.status === 'failed' || log.status === 'error' ? (
                          <Badge variant="destructive">{t('candidates.sync.statusError', 'Échec')}</Badge>
                        ) : (
                          <Badge variant="outline">{log.status}</Badge>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}


